import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Music2, Sparkles, Gift, Calendar, MessageSquare, ShieldCheck, ArrowRight, Mic, Headphones } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const Join = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToSms, setAgreedToSms] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: new URLSearchParams(window.location.search).get('email') || '',
    phone: '',
    password: ''
  });

  useEffect(() => {
    document.title = 'Join High Quality Studios — Brooklyn Recording Studio';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy.');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        ...formData,
        sms_consent: agreedToSms === true,
        sms_consent_text: agreedToSms
          ? 'I agree to receive booking confirmation, approval, and appointment reminder text messages from High Quality Studios. Msg & data rates may apply. Reply STOP to opt out.'
          : null,
        sms_consent_timestamp: agreedToSms ? new Date().toISOString() : null,
        terms_agreed: true,
      });

      if (res.data?.session_token) {
        localStorage.setItem('session_token', res.data.session_token);
        localStorage.setItem('user_data', JSON.stringify(res.data.user));
      }
      setStep('success');
      toast.success('Welcome to HQ Studios — account created!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Signup failed. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Background gradient blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 right-1/4 w-[500px] h-[500px] bg-pink-600/15 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-8 sm:py-12 min-h-screen flex flex-col">
        {/* Tiny header — keep it shareable */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" data-testid="join-back-home" className="flex items-center gap-2 text-purple-300 hover:text-white transition-colors">
            <Music2 className="w-5 h-5" />
            <span className="font-bold tracking-wider text-sm">HQ STUDIOS</span>
          </Link>
          <Link to="/" className="text-xs text-gray-400 hover:text-purple-300">View site →</Link>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 flex-1 items-center">
          {/* LEFT — Pitch */}
          <div className="lg:col-span-3 space-y-6 sm:space-y-8">
            <div className="inline-flex items-center gap-2 bg-purple-500/15 border border-purple-400/30 rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold tracking-wide text-purple-200">
              <Sparkles className="w-3.5 h-3.5" />
              <span>FREE TO JOIN · NO CREDIT CARD</span>
            </div>

            <div
              data-testid="signup-bonus-banner"
              className="flex items-start gap-3 px-4 py-3 bg-gradient-to-r from-amber-500/20 via-pink-500/20 to-purple-500/20 border border-amber-400/40 rounded-xl max-w-xl"
            >
              <Gift className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
              <div className="text-sm leading-tight">
                <div className="font-bold text-amber-100">Limited: 3 Free Credits on Signup</div>
                <div className="text-amber-100/80 text-xs mt-0.5">
                  Sign up by Wed, Oct 8 and get 3 free studio credits dropped straight into your account.
                </div>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]">
              Studio time,<br />
              <span className="bg-gradient-to-r from-purple-300 via-pink-300 to-amber-300 bg-clip-text text-transparent">
                on tap.
              </span>
            </h1>

            <p className="text-base sm:text-lg text-gray-300 max-w-xl leading-relaxed">
              Sign up and unlock instant booking, exclusive deals, and free studio credits
              — at Brooklyn's most-booked recording studio.
            </p>

            <ul className="space-y-3 max-w-md">
              {[
                { icon: Gift, text: '$150 Early Bird (6 hrs) — members only' },
                { icon: Calendar, text: 'Book in 30 seconds with saved info' },
                { icon: Mic, text: 'Free hour for every 3 hours booked' },
                { icon: MessageSquare, text: 'SMS reminders so you never miss a session' },
                { icon: ShieldCheck, text: 'No spam. Reply STOP anytime.' },
              ].map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-200">
                  <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-purple-500/15 border border-purple-400/30 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-purple-300" />
                  </span>
                  <span className="text-sm sm:text-base">{text}</span>
                </li>
              ))}
            </ul>

            <div className="hidden lg:flex items-center gap-3 pt-6 text-sm text-gray-400">
              <Headphones className="w-4 h-4" />
              <span>East Williamsburg, Brooklyn · Montrose Av (L) · Open daily</span>
            </div>
          </div>

          {/* RIGHT — Form card */}
          <div className="lg:col-span-2">
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl shadow-purple-900/20">
              {step === 'form' ? (
                <>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-white mb-1">Create your account</h2>
                    <p className="text-sm text-gray-400">Free forever. Takes 30 seconds.</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3" data-testid="join-form">
                    <input
                      type="text"
                      placeholder="Full Name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="join-name"
                      className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="join-email"
                      className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                    <input
                      type="tel"
                      placeholder="Phone Number"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="join-phone"
                      className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                    <input
                      type="password"
                      placeholder="Password (min 6 characters)"
                      required
                      minLength={6}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      data-testid="join-password"
                      className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />

                    <label className="flex items-start gap-2 p-3 bg-purple-500/10 border border-purple-400/20 rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="join-terms-checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-purple-500 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-xs text-gray-300 leading-relaxed">
                        I agree to the{' '}
                        <Link to="/terms" target="_blank" className="text-purple-300 underline font-semibold">Terms</Link>
                        {' '}and{' '}
                        <Link to="/privacy" target="_blank" className="text-purple-300 underline font-semibold">Privacy Policy</Link>.
                      </span>
                    </label>

                    <label className="flex items-start gap-2 p-3 bg-purple-500/5 border border-purple-400/20 rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="join-sms-checkbox"
                        checked={agreedToSms}
                        onChange={(e) => setAgreedToSms(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-purple-500 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-xs text-gray-300 leading-relaxed">
                        <span className="text-purple-300 font-semibold">(Optional)</span> I agree to receive booking confirmation, approval, and reminder
                        text messages from <span className="font-semibold">High Quality Studios</span> at the phone number provided. Message frequency varies. Msg &amp; data rates may apply.
                        Reply <span className="font-mono bg-purple-900/40 px-1 rounded">STOP</span> to opt out, <span className="font-mono bg-purple-900/40 px-1 rounded">HELP</span> for help. Consent is not a condition of account creation.
                      </span>
                    </label>

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="join-submit"
                      className="w-full mt-2 flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:opacity-60 rounded-xl font-bold text-white transition-all hover:scale-[1.01] shadow-lg shadow-purple-500/30"
                    >
                      {loading ? 'Creating account…' : (
                        <>
                          Create Account
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>

                    <p className="text-center text-xs text-gray-500 pt-1">
                      Already have an account?{' '}
                      <Link to="/" className="text-purple-300 hover:text-white underline" data-testid="join-login-link">
                        Log in
                      </Link>
                    </p>
                  </form>
                </>
              ) : (
                <div className="text-center py-6 space-y-5">
                  <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">You're in.</h2>
                  <p className="text-gray-300">
                    Account created. Time to book your first session and try the Early Bird deal.
                  </p>
                  <button
                    onClick={() => navigate('/#booking')}
                    data-testid="join-book-now-btn"
                    className="w-full px-6 py-3.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-xl font-bold text-white"
                  >
                    Book a Session →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-gray-500">
          Brought to you by Trapspade · High Quality Studios LLC ·{' '}
          <a href="tel:9294131993" className="text-purple-300 underline">929-413-1993</a>
        </div>
      </div>
    </div>
  );
};

export default Join;
